import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/contexts/ProfileContext';
import { supabase } from '@/integrations/supabase/client';
import { getPlanFeatures, isUnlimited } from '@/constants/plans';
import { debugLogger } from '@/utils/debugLogger';

interface ProfileViewLimits {
  current: number;
  limit: number;
  remaining: number;
  canView: boolean;
  isUnlimited: boolean;
}

export const useProfileViewLimits = () => {
  const { user } = useAuth();
  const { profile: userProfile } = useProfile();
  const [viewLimits, setViewLimits] = useState<ProfileViewLimits>({
    current: 0,
    limit: 30,
    remaining: 30,
    canView: true,
    isUnlimited: false
  });
  const [loading, setLoading] = useState(true);

  const checkViewLimits = useCallback(async () => {
    if (!user?.id || !userProfile) return;

    try {
      setLoading(true);

      const planType = userProfile.plan_type || 'sponsor_free';
      const features = getPlanFeatures(planType);
      const dailyLimit = features.profile_views_daily;

      // Count today's profile views
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const { count, error } = await supabase
        .from('profile_views')
        .select('*', { count: 'exact', head: true })
        .eq('viewer_id', user.id)
        .gte('created_at', today.toISOString());

      if (error) {
        debugLogger.error('ProfileViewLimits', 'Error counting views:', error);
        return;
      }

      const currentCount = count || 0;
      const unlimited = isUnlimited(dailyLimit);
      const remaining = unlimited ? -1 : Math.max(0, dailyLimit - currentCount);
      const canView = unlimited || remaining > 0;

      setViewLimits({
        current: currentCount,
        limit: dailyLimit,
        remaining,
        canView,
        isUnlimited: unlimited
      });

      debugLogger.debug('ProfileViewLimits', 'View limits updated:', {
        current: currentCount,
        limit: dailyLimit,
        remaining,
        canView,
        isUnlimited: unlimited
      });

    } catch (error) {
      debugLogger.error('ProfileViewLimits', 'Error checking view limits:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id, userProfile]);

  const incrementViewCount = useCallback(() => {
    setViewLimits(prev => {
      if (prev.isUnlimited) return prev;
      
      const newCurrent = prev.current + 1;
      const newRemaining = Math.max(0, prev.limit - newCurrent);
      
      return {
        ...prev,
        current: newCurrent,
        remaining: newRemaining,
        canView: newRemaining > 0
      };
    });
  }, []);

  useEffect(() => {
    checkViewLimits();
  }, [checkViewLimits]);

  return {
    viewLimits,
    loading,
    checkViewLimits,
    incrementViewCount
  };
};
